import tierConfiguration from './tierConfiguration.json'
import pricingCatalog from '../components/Settings/pricingCatalog.json'
import { testingOverrideAllowsAllFeatures } from './testingOverrides'
import {
  resolveTierLimit,
  getPlanLimitOverrides,
  type FeatureKey,
  type PlanLimitOverrides,
  TIER_IDS,
  type TierId,
  LEGACY_TIER_MAP,
  normalizeTierId,
} from './planLimitsCore'

export {
  TIER_IDS,
  type TierId,
  LEGACY_TIER_MAP,
  normalizeTierId,
}

export type BillingCycle = 'monthly' | 'yearly'
// mirror of functions-common/src/config/featureTiers.ts; keep in sync
export type GatedFeature =
  | 'AI_GENERATION'
  | 'FULL_AUDIT'
  | 'STORAGE_HUB'
  | 'MULTIPLAYER_SYNC'
  | 'WHITE_LABEL'
  | 'TIMESHEETS'
  | 'TIMESHEETS_MONEY'

// Purchase rollout flag: flip this to false when pricing should remain visible
// but all purchase CTAs and paid-plan enforcement need to stay disabled for testing.
export const ENABLE_PURCHASE_PLANS = true;

// mirror of functions-common/src/config/featureTiers.ts; keep in sync
export const FEATURE_MINIMUM_TIER: Record<GatedFeature, TierId> = {
  AI_GENERATION: 'ESSENTIAL_TIER',
  FULL_AUDIT: 'PROFESSIONAL_TIER',
  STORAGE_HUB: 'ESSENTIAL_TIER',
  MULTIPLAYER_SYNC: 'PROFESSIONAL_TIER',
  WHITE_LABEL: 'ENTERPRISE_TIER',
  TIMESHEETS: 'ESSENTIAL_TIER',
  TIMESHEETS_MONEY: 'PROFESSIONAL_TIER',
}

const TIER_RANK = Object.fromEntries(TIER_IDS.map((tier, index) => [tier, index])) as Record<TierId, number>
export const hasTierFeature = (plan: unknown, feature: GatedFeature) =>
  testingOverrideAllowsAllFeatures() || TIER_RANK[normalizeTierId(plan)] >= TIER_RANK[FEATURE_MINIMUM_TIER[feature]]

// Public plan definitions are derived from pricingCatalog.json — the single source of truth for
// prices, credits, seat allowances, and per-tier accents. Downstream UI reads through this shape.
export type PricingTierDefinition = {
  id: TierId
  name: string
  monthlyPrice: number | null
  yearlyPrice: number | null
  monthlyCredits: number
  yearlyCredits: number
  includedMembers: number
  includedHelpers: number
  memberSeatRate: number
  helperSeatRate: number
  accent: string
  description: string
  recommended?: boolean
  badge?: string
}

type CatalogTeamPlan = typeof pricingCatalog.teamPlans[number]

const coerceNumber = (value: unknown, fallback = 0): number => typeof value === 'number' ? value : fallback

const toTierDefinition = (plan: CatalogTeamPlan): PricingTierDefinition => ({
  id: plan.id as TierId,
  name: plan.name,
  monthlyPrice: plan.monthlyPrice as number | null,
  yearlyPrice: plan.yearlyPrice as number | null,
  monthlyCredits: coerceNumber(plan.monthlyCredits),
  yearlyCredits: coerceNumber((plan as { yearlyCredits?: unknown }).yearlyCredits),
  includedMembers: coerceNumber(plan.includedMembers),
  includedHelpers: coerceNumber(plan.includedHelpers),
  memberSeatRate: coerceNumber(plan.memberSeatRate),
  helperSeatRate: coerceNumber(plan.helperSeatRate),
  accent: plan.accent,
  description: (plan as { description?: string }).description ?? plan.utilityLabel,
  recommended: (plan as { recommended?: boolean }).recommended,
  badge: (plan as { badge?: string }).badge,
})

export const PRICING_TIERS: readonly PricingTierDefinition[] = pricingCatalog.teamPlans.map(toTierDefinition)

export const MAX_ADDITIONAL_MEMBERS = 100
export const MAX_ADDITIONAL_HELPERS = 250
export const getTierLimits = (
  plan: unknown,
  overrides: PlanLimitOverrides = getPlanLimitOverrides(),
): typeof tierConfiguration.tiers.ENTERPRISE_TIER.limits => {
  if (testingOverrideAllowsAllFeatures()) {
    return Object.fromEntries(
      Object.keys(tierConfiguration.tiers.ENTERPRISE_TIER.limits).map((key) => [key, -1]),
    ) as typeof tierConfiguration.tiers.ENTERPRISE_TIER.limits
  }
  const tier = normalizeTierId(plan)
  const baseLimits = tierConfiguration.tiers[tier].limits
  return Object.fromEntries(
    (Object.keys(baseLimits) as FeatureKey[]).map((key) => [
      key,
      resolveTierLimit(tier, key, overrides),
    ]),
  ) as typeof tierConfiguration.tiers.ENTERPRISE_TIER.limits
}
